import { cur } from '@/lib/currency';
import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { DateFilter } from '@/components/DateFilter';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Users, DollarSign, Clock, Calendar, BarChart2, PieChart as PieChartIcon, BarChart3, UserPlus, TrendingUp, AlertTriangle, Building2, Wallet } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#f97316', '#84cc16', '#ec4899', '#6366f1'];

export default function HRAnalyticsPage() {
  const { t } = useLanguage();
  const [headcount, setHeadcount] = useState(null);
  const [dateFilter, setDateFilter] = useState({ start: null, end: null, period: "all" });
  const [payrollCost, setPayrollCost] = useState(null);
  const [attendance, setAttendance] = useState(null);
  const [leaveUtil, setLeaveUtil] = useState(null);
  const [costPerHire, setCostPerHire] = useState(null);
  const [salarySnapshot, setSalarySnapshot] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const [hcRes, pcRes, atRes, luRes, cphRes, ssRes] = await Promise.all([
        api.get('/hr-analytics/headcount'),
        api.get('/hr-analytics/payroll-cost'),
        api.get('/hr-analytics/attendance'),
        api.get('/hr-analytics/leave-utilization'),
        api.get('/recruitment/cost-per-hire'),
        api.get('/hr-analytics/salary-snapshot'),
      ]);
      setHeadcount(hcRes.data);
      setPayrollCost(pcRes.data);
      setAttendance(atRes.data);
      setLeaveUtil(luRes.data);
      setCostPerHire(cphRes.data);
      setSalarySnapshot(ssRes.data);
    } catch (err) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="hr-analytics-page">
        <div>
          <div className="flex items-center gap-4">

            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg shadow-violet-200/50">

              <BarChart3 size={24} className="text-white" />

            </div>

            <div>

              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="page-title">{t('hr_analytics_title') || 'HR Analytics & Reports'}</h1>
            <DateFilter onFilterChange={setDateFilter} />

              <p className="text-sm text-muted-foreground mt-0.5">HR analytics and insights</p>

            </div>

          </div>
          <p className="text-muted-foreground text-sm mt-1">Headcount, payroll cost, attendance, and leave utilization insights.</p>
        </div>

        {/* Top KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card data-testid="kpi-headcount"><CardContent className="pt-4 pb-3"><div className="flex items-center gap-3"><div className="p-2 bg-info/10 rounded-lg"><Users size={18} className="text-info" /></div><div><div className="text-2xl font-bold">{headcount?.total || 0}</div><div className="text-xs text-muted-foreground">Total Headcount</div></div></div></CardContent></Card>
          <Card data-testid="kpi-monthly-payroll"><CardContent className="pt-4 pb-3"><div className="flex items-center gap-3"><div className="p-2 bg-success/10 rounded-lg"><DollarSign size={18} className="text-success" /></div><div><div className="text-2xl font-bold">{payrollCost?.total_monthly_salary?.toLocaleString() || 0}</div><div className="text-xs text-muted-foreground">Monthly Payroll</div></div></div></CardContent></Card>
          <Card data-testid="kpi-cost-per-hire" className="border-2 border-error/30 bg-gradient-to-br from-rose-50/50 to-white">
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-error/20 rounded-lg"><UserPlus size={18} className="text-error" /></div>
                <div>
                  <div className="text-2xl font-bold text-error">{cur()} {(costPerHire?.cost_per_hire || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</div>
                  <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                    Cost / Hire
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-error/20 text-error font-medium">{costPerHire?.hire_count || 0} hired</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="kpi-attendance"><CardContent className="pt-4 pb-3"><div className="flex items-center gap-3"><div className="p-2 bg-warning/10 rounded-lg"><Clock size={18} className="text-warning" /></div><div><div className="text-2xl font-bold">{attendance?.total_records || 0}</div><div className="text-xs text-muted-foreground">Attendance Records</div></div></div></CardContent></Card>
          <Card data-testid="kpi-leave"><CardContent className="pt-4 pb-3"><div className="flex items-center gap-3"><div className="p-2 bg-purple-50 rounded-lg"><Calendar size={18} className="text-purple-600" /></div><div><div className="text-2xl font-bold">{leaveUtil?.total_leave_days || 0}</div><div className="text-xs text-muted-foreground">Total Leave Days</div></div></div></CardContent></Card>
        </div>

        {/* iter879 — Salary Snapshot widget */}
        {salarySnapshot && (
          <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50/40 to-white" data-testid="salary-snapshot-widget">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Wallet size={16} className="text-emerald-600" /> Salary Snapshot
                <span className="ml-auto text-[11px] font-normal text-muted-foreground">
                  {salarySnapshot.payroll_ready_employees} of {salarySnapshot.active_employees} employees ready for payroll
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="rounded-xl border bg-emerald-100/40 border-emerald-200 px-3 py-2">
                  <div className="text-[10px] uppercase tracking-wide text-emerald-700 opacity-80">Next-payroll estimate</div>
                  <div className="text-2xl font-bold text-emerald-800">{cur()} {salarySnapshot.next_payroll_estimate.toLocaleString()}</div>
                </div>
                <div className="rounded-xl border bg-teal-50 border-teal-200 px-3 py-2">
                  <div className="text-[10px] uppercase tracking-wide text-teal-700 opacity-80">Annual cost</div>
                  <div className="text-2xl font-bold text-teal-800">{cur()} {salarySnapshot.total_annual_cost.toLocaleString()}</div>
                </div>
                <div className="rounded-xl border bg-blue-50 border-blue-200 px-3 py-2">
                  <div className="text-[10px] uppercase tracking-wide text-blue-700 opacity-80">Active</div>
                  <div className="text-2xl font-bold text-blue-800">{salarySnapshot.active_employees}</div>
                </div>
                <div className={`rounded-xl border px-3 py-2 ${salarySnapshot.zero_salary_employees.length > 0 ? 'bg-rose-50 border-rose-200' : 'bg-slate-50 border-slate-200'}`} data-testid="zero-salary-count">
                  <div className={`text-[10px] uppercase tracking-wide opacity-80 ${salarySnapshot.zero_salary_employees.length > 0 ? 'text-rose-700' : 'text-slate-600'}`}>Salary = 0 (payroll skips)</div>
                  <div className={`text-2xl font-bold ${salarySnapshot.zero_salary_employees.length > 0 ? 'text-rose-800' : 'text-slate-700'}`}>{salarySnapshot.zero_salary_employees.length}</div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Per-branch breakdown */}
                <div className="border rounded-lg overflow-hidden">
                  <div className="px-3 py-2 bg-secondary/40 text-[11px] font-semibold flex items-center gap-1.5">
                    <Building2 size={12} /> Cost per branch
                  </div>
                  <div className="divide-y max-h-52 overflow-y-auto">
                    {salarySnapshot.per_branch.length === 0 ? (
                      <div className="p-3 text-xs text-muted-foreground">No branches configured.</div>
                    ) : salarySnapshot.per_branch.map((b) => (
                      <div key={b.branch_id} className="px-3 py-2 flex justify-between items-center text-xs" data-testid={`branch-cost-${b.branch_id}`}>
                        <div>
                          <div className="font-medium">{b.name}</div>
                          <div className="text-[10px] text-muted-foreground">{b.employees} employee{b.employees === 1 ? '' : 's'}</div>
                        </div>
                        <div className="font-mono font-semibold text-emerald-700">{cur()} {b.total.toLocaleString()}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Zero-salary red flag list */}
                {salarySnapshot.zero_salary_employees.length > 0 ? (
                  <div className="border border-rose-200 rounded-lg overflow-hidden">
                    <div className="px-3 py-2 bg-rose-50 text-[11px] font-semibold text-rose-800 flex items-center gap-1.5">
                      <AlertTriangle size={12} /> Fix these before payroll
                    </div>
                    <div className="divide-y max-h-52 overflow-y-auto">
                      {salarySnapshot.zero_salary_employees.slice(0, 20).map((e) => (
                        <div key={e.id} className="px-3 py-2 text-xs" data-testid={`zero-salary-${e.id}`}>
                          <div className="font-medium">{e.name}</div>
                          <div className="text-[10px] text-muted-foreground">{e.position} · {e.branch}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="border border-emerald-200 rounded-lg overflow-hidden">
                    <div className="px-3 py-2 bg-emerald-50 text-[11px] font-semibold text-emerald-800 flex items-center gap-1.5">
                      <TrendingUp size={12} /> Avg salary by role
                    </div>
                    <div className="divide-y max-h-52 overflow-y-auto">
                      {salarySnapshot.avg_by_role.length === 0 ? (
                        <div className="p-3 text-xs text-muted-foreground">No employees yet.</div>
                      ) : salarySnapshot.avg_by_role.slice(0, 10).map((r) => (
                        <div key={r.role} className="px-3 py-2 flex justify-between text-xs">
                          <div>
                            <div className="font-medium">{r.role}</div>
                            <div className="text-[10px] text-muted-foreground">{r.count} employee{r.count === 1 ? '' : 's'}</div>
                          </div>
                          <div className="text-right">
                            <div className="font-mono font-semibold">{cur()} {r.avg_salary.toLocaleString()}</div>
                            <div className="text-[10px] text-muted-foreground">avg</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue="headcount" className="space-y-4">
          <TabsList data-testid="analytics-tabs">
            <TabsTrigger value="headcount">Headcount</TabsTrigger>
            <TabsTrigger value="payroll">Payroll Cost</TabsTrigger>
            <TabsTrigger value="recruitment">Recruitment</TabsTrigger>
            <TabsTrigger value="attendance">Attendance</TabsTrigger>
            <TabsTrigger value="leave">Leave Utilization</TabsTrigger>
          </TabsList>

          <TabsContent value="headcount">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardHeader><CardTitle className="text-base">By Department</CardTitle></CardHeader>
                <CardContent className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={headcount?.by_department || []}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="name" fontSize={11} /><YAxis fontSize={11} /><Tooltip /><Bar dataKey="count" fill="#3b82f6" radius={[4,4,0,0]} /></BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle className="text-base">By Nationality</CardTitle></CardHeader>
                <CardContent className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart><Pie data={headcount?.by_nationality || []} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={({name, count}) => `${name}: ${count}`} labelLine={true}>
                      {(headcount?.by_nationality || []).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie><Tooltip /><Legend /></PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle className="text-base">By Gender</CardTitle></CardHeader>
                <CardContent className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart><Pie data={headcount?.by_gender || []} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                      {(headcount?.by_gender || []).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie><Tooltip /><Legend /></PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle className="text-base">By Contract Type</CardTitle></CardHeader>
                <CardContent className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={headcount?.by_contract_type || []}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="name" fontSize={11} /><YAxis fontSize={11} /><Tooltip /><Bar dataKey="count" fill="#8b5cf6" radius={[4,4,0,0]} /></BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="payroll">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardHeader><CardTitle className="text-base">Monthly Cost by Department</CardTitle></CardHeader>
                <CardContent className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={payrollCost?.by_department || []}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="name" fontSize={11} /><YAxis fontSize={11} /><Tooltip formatter={(v) => v.toLocaleString()} /><Bar dataKey="cost" fill="#10b981" radius={[4,4,0,0]} /></BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle className="text-base">Payroll Run History</CardTitle></CardHeader>
                <CardContent className="h-72">
                  {(payrollCost?.payroll_history || []).length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={payrollCost.payroll_history}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="period" fontSize={11} /><YAxis fontSize={11} /><Tooltip formatter={(v) => v.toLocaleString()} /><Bar dataKey="gross" fill="#3b82f6" name="Gross" radius={[4,4,0,0]} /><Bar dataKey="net" fill="#10b981" name="Net" radius={[4,4,0,0]} /></BarChart>
                    </ResponsiveContainer>
                  ) : <div className="flex items-center justify-center h-full text-sm text-muted-foreground">No payroll history yet</div>}
                </CardContent>
              </Card>
              <Card className="md:col-span-2">
                <CardContent className="pt-4">
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div className="bg-success/10 rounded-lg p-4"><div className="text-2xl font-bold text-success">{payrollCost?.total_monthly_salary?.toLocaleString()}</div><div className="text-xs text-muted-foreground">Monthly Total</div></div>
                    <div className="bg-info/10 rounded-lg p-4"><div className="text-2xl font-bold text-info">{payrollCost?.total_annual_salary?.toLocaleString()}</div><div className="text-xs text-muted-foreground">Annual Total</div></div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="recruitment">
            <div className="space-y-4" data-testid="recruitment-tab">
              {/* Hero KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="border-2 border-error/30">
                  <CardContent className="pt-4 pb-3 text-center">
                    <UserPlus size={22} className="text-error mx-auto mb-1" />
                    <div className="text-3xl font-bold text-error">{cur()} {(costPerHire?.cost_per_hire || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                    <div className="text-xs text-muted-foreground mt-1">Avg Cost per Hire</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 pb-3 text-center">
                    <TrendingUp size={22} className="text-success mx-auto mb-1" />
                    <div className="text-3xl font-bold text-success">{costPerHire?.hire_count || 0}</div>
                    <div className="text-xs text-muted-foreground mt-1">Hires in Period</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 pb-3 text-center">
                    <DollarSign size={22} className="text-info mx-auto mb-1" />
                    <div className="text-3xl font-bold text-info">{cur()} {(costPerHire?.total_recruitment_cost || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                    <div className="text-xs text-muted-foreground mt-1">Total Spend</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 pb-3 text-center">
                    <BarChart3 size={22} className="text-violet-500 mx-auto mb-1" />
                    <div className="text-3xl font-bold text-violet-700">{costPerHire?.fees_count || 0}</div>
                    <div className="text-xs text-muted-foreground mt-1">Fee Entries</div>
                  </CardContent>
                </Card>
              </div>

              {/* Breakdown */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader><CardTitle className="text-base">Spend by Fee Type</CardTitle></CardHeader>
                  <CardContent>
                    {!costPerHire?.by_fee_type?.length ? (
                      <p className="text-xs text-muted-foreground italic py-6 text-center">No fee data yet. Record a recruitment fee from the Recruitment page.</p>
                    ) : (
                      <ResponsiveContainer width="100%" height={240}>
                        <BarChart data={costPerHire.by_fee_type}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="fee_type" fontSize={10} tickFormatter={v => v.replace(/_/g, ' ')} />
                          <YAxis fontSize={11} />
                          <Tooltip formatter={(v) => `${cur()} ${Number(v).toLocaleString()}`} />
                          <Bar dataKey="total" fill="#f43f5e" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader><CardTitle className="text-base">Cost per Hire by Branch</CardTitle></CardHeader>
                  <CardContent className="p-0">
                    {!costPerHire?.by_branch?.length ? (
                      <p className="text-xs text-muted-foreground italic py-6 text-center">No branch-level hires recorded yet.</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs" data-testid="cph-by-branch">
                          <thead className="bg-muted/50 border-b border-border">
                            <tr>
                              <th className="text-left px-3 py-2 font-semibold text-muted-foreground">Branch</th>
                              <th className="text-center px-3 py-2 font-semibold text-muted-foreground">Hires</th>
                              <th className="text-right px-3 py-2 font-semibold text-muted-foreground">Total Spend</th>
                              <th className="text-right px-3 py-2 font-semibold text-muted-foreground">Cost/Hire</th>
                            </tr>
                          </thead>
                          <tbody>
                            {costPerHire.by_branch.map(b => (
                              <tr key={b.branch_id} className="border-b border-border">
                                <td className="px-3 py-2 font-medium text-foreground">{b.branch_name}</td>
                                <td className="px-3 py-2 text-center tabular-nums">{b.hire_count}</td>
                                <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{cur()} {b.fees_total.toLocaleString()}</td>
                                <td className="px-3 py-2 text-right tabular-nums font-bold text-error">
                                  {b.hire_count > 0 ? `${cur()} ${b.cost_per_hire.toLocaleString()}` : '—'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardContent className="p-4 bg-gradient-to-br from-rose-50 to-white text-xs text-muted-foreground">
                  <div className="flex items-start gap-2">
                    <UserPlus size={14} className="text-error mt-0.5 shrink-0" />
                    <div>
                      <p className="font-semibold text-foreground mb-1">How is Cost per Hire calculated?</p>
                      <p>Total recruitment-fee expenses (agency, visa, iqama, medical, ticket, onboarding, placement, etc.) ÷ number of applicants moved to <span className="font-mono bg-muted px-1.5 rounded">hired</span> stage within the period. Fees flow from the <span className="font-semibold">Recruitment Fee dialog</span> on each applicant and are also recorded as P&L expenses under &quot;Recruitment Fee&quot; category.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="attendance">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card><CardContent className="pt-4 text-center"><div className="text-3xl font-bold">{attendance?.total_records || 0}</div><div className="text-xs text-muted-foreground">Total Records</div></CardContent></Card>
              <Card><CardContent className="pt-4 text-center"><div className="text-3xl font-bold text-warning">{attendance?.late_rate || 0}%</div><div className="text-xs text-muted-foreground">Late Rate</div></CardContent></Card>
              <Card><CardContent className="pt-4 text-center"><div className="text-3xl font-bold text-success">{attendance?.total_overtime || 0}h</div><div className="text-xs text-muted-foreground">Total Overtime</div></CardContent></Card>
            </div>
            <Card className="mt-4">
              <CardHeader><CardTitle className="text-base">Employee Attendance Summary</CardTitle></CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="att-summary-table">
                    <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="pb-2 pr-4">Employee</th><th className="pb-2 pr-4 text-center">Days</th><th className="pb-2 pr-4 text-center">Hours</th><th className="pb-2 pr-4 text-center">Overtime</th><th className="pb-2 text-center">Late</th></tr></thead>
                    <tbody>
                      {(attendance?.by_employee || []).map((e, i) => (
                        <tr key={i} className="border-b last:border-0 hover:bg-muted/50">
                          <td className="py-2 pr-4 font-medium">{e.name}</td>
                          <td className="py-2 pr-4 text-center">{e.days}</td>
                          <td className="py-2 pr-4 text-center">{e.hours?.toFixed(1)}</td>
                          <td className="py-2 pr-4 text-center text-success">{e.overtime?.toFixed(1)}h</td>
                          <td className="py-2 text-center text-error">{e.late_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="leave">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardHeader><CardTitle className="text-base">Leave Days by Type</CardTitle></CardHeader>
                <CardContent className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart><Pie data={leaveUtil?.by_type || []} dataKey="days" nameKey="type" cx="50%" cy="50%" outerRadius={90} label>
                      {(leaveUtil?.by_type || []).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie><Tooltip /><Legend /></PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle className="text-base">Employee Leave Utilization</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {(leaveUtil?.employee_utilization || []).map((e, i) => (
                      <div key={i} className="flex items-center gap-3 text-sm">
                        <span className="w-28 truncate font-medium">{e.name}</span>
                        <div className="flex-1 bg-muted rounded-full h-2.5">
                          <div className="bg-primary rounded-full h-2.5" style={{ width: `${Math.min(e.utilization_rate, 100)}%` }} />
                        </div>
                        <span className="text-xs w-20 text-right">{e.annual_used}/{e.annual_entitled} ({e.utilization_rate}%)</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
